// src/services/booking/persistence.service.js

import moment from 'moment';

/**
 * Service de gestion de la persistance des données de réservation
 * Responsable de la sauvegarde et du chargement depuis sessionStorage
 * 
 * ✅ Utilise sessionStorage : les données sont automatiquement supprimées à la fermeture de l'onglet
 */
class PersistenceService {
    constructor() {
        this.STORAGE_PREFIX = 'sportips_booking_';
        this.EXPIRES_PREFIX = 'sportips_booking_expires_';
        this.EXPIRATION_TIME = 10; // minutes
    }

    /**
     * Génère la clé de stockage pour un module
     * @param {string} moduleBase - La base du module
     * @returns {string} La clé de stockage
     */
    _getStorageKey(moduleBase) {
        return `${this.STORAGE_PREFIX}${moduleBase}`;
    }

    /**
     * Génère la clé d'expiration pour un module
     * @param {string} moduleBase - La base du module
     * @returns {string} La clé d'expiration
     */
    _getExpiresKey(moduleBase) {
        return `${this.EXPIRES_PREFIX}${moduleBase}`;
    }

    /**
     * Sauvegarde une réservation dans le sessionStorage
     * @param {Object} booking - L'état de la réservation à sauvegarder
     * @param {string} moduleBase - La base du module
     */
    save(booking, moduleBase) {
        const storageKey = this._getStorageKey(moduleBase);
        const expiresKey = this._getExpiresKey(moduleBase);

        // Créer une copie des données à sauvegarder
        const bookingData = { ...booking };

        // Stocker les données en JSON
        sessionStorage.setItem(storageKey, JSON.stringify(bookingData));
        sessionStorage.setItem(expiresKey, moment().add(this.EXPIRATION_TIME, 'minutes').toISOString());
    }

    /**
     * Charge une réservation depuis le sessionStorage si elle existe et n'est pas expirée
     * @param {string} moduleBase - La base du module
     * @returns {Object|null} Les données de la réservation ou null si inexistante/expirée
     */
    load(moduleBase) {
        const storageKey = this._getStorageKey(moduleBase);
        const expiresKey = this._getExpiresKey(moduleBase);

        const storedData = sessionStorage.getItem(storageKey);
        const expiresAt = sessionStorage.getItem(expiresKey);

        if (!storedData) {
            return null;
        }

        // Vérifier l'expiration
        if (expiresAt && moment().isBefore(moment(expiresAt))) {
            try {
                return JSON.parse(storedData);
            } catch (error) {
                console.error('Erreur lors du chargement des données:', error);
                // Si le chargement échoue, supprimer les données corrompues
                this.clear(moduleBase);
                return null;
            }
        }

        // Données expirées, les supprimer
        this.clear(moduleBase);
        return null;
    }

    /**
     * Supprime les données de réservation du sessionStorage pour un module
     * @param {string} moduleBase - La base du module
     */
    clear(moduleBase) {
        const storageKey = this._getStorageKey(moduleBase);
        const expiresKey = this._getExpiresKey(moduleBase);
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(expiresKey);
    }

    /**
     * Supprime toutes les données de réservation sauf celles du module courant
     * @param {string} moduleBase - La base du module à conserver
     */
    clearOtherModules(moduleBase) {
        // Créer une copie des clés car sessionStorage peut changer pendant l'itération
        const keysToCheck = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            keysToCheck.push(sessionStorage.key(i));
        }

        for (const key of keysToCheck) {
            // Garder les clés qui contiennent le moduleBase
            if (key && key.includes(moduleBase)) {
                continue;
            }
            // Supprimer les clés de réservation qui ne correspondent pas au module courant
            if (key && (key.startsWith(this.STORAGE_PREFIX) || key.startsWith(this.EXPIRES_PREFIX))) {
                sessionStorage.removeItem(key);
            }
        }
    }

    /**
     * Vérifie si des données existent pour un module
     * @param {string} moduleBase - La base du module
     * @returns {boolean} True si des données existent et ne sont pas expirées
     */
    hasData(moduleBase) {
        return this.load(moduleBase) !== null;
    }
}

export default new PersistenceService();

